import type { UserOfficeAffiliation } from "le-coffre-resources/dist/Notary";
import UserOfficesApi from "@Front/Api/LeCoffreApi/Notary/UserOffices/UserOffices";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { triggerNotaryEntitlementRefresh } from "@Front/Utils/notaryEntitlementRefreshBridge";
import { applyIdNotSyncedAffiliations } from "./activeOfficeStoreIdNotSyncHelpers";

export type ActiveOfficeStoreLoadSyncTarget = {
	officeSwitchInProgress: boolean;
	availableOffices: UserOfficeAffiliation[];
	error: string | null;
	isLoading: boolean;
	activeOffice: UserOfficeAffiliation | null;
	ensureUserOfficesApiAvailable(): void;
	applyResolvedActiveOffice(offices: UserOfficeAffiliation[]): void;
};

export async function loadUserOfficesFromDatabase(store: ActiveOfficeStoreLoadSyncTarget, userUid: string): Promise<UserOfficeAffiliation[]> {
	if (store.officeSwitchInProgress) {
		LoggerService.info("⏸️ loadUserOfficesFromDB ignoré (changement d'office en cours)");
		return store.availableOffices;
	}

	try {
		store.ensureUserOfficesApiAvailable();
		const offices = await UserOfficesApi.getInstance().getUserOffices(userUid);

		if (!Array.isArray(offices)) {
			LoggerService.error("❌ getUserOffices n'a pas retourné un tableau", { type: typeof offices });
			store.availableOffices = [];
			return [];
		}

		LoggerService.info("🔍 Offices chargés depuis la base", { count: offices.length });
		store.availableOffices = offices;
		store.applyResolvedActiveOffice(offices);
		LoggerService.info("✅ Office actif déterminé", {
			activeOffice: store.activeOffice?.office?.name ?? "Aucun",
			count: offices.length,
		});
		return offices;
	} catch (error: unknown) {
		LoggerService.error("❌ Erreur lors du chargement des offices", { error, userUid });
		store.error = "Impossible de charger les offices";
		store.availableOffices = [];
		return [];
	}
}

export async function syncUserOfficesFromIdNot(store: ActiveOfficeStoreLoadSyncTarget, userUid: string): Promise<void> {
	store.isLoading = true;
	store.error = null;

	try {
		store.ensureUserOfficesApiAvailable();
		const affiliations = await UserOfficesApi.getInstance().syncUserOffices(userUid);

		if (!Array.isArray(affiliations)) {
			LoggerService.error("❌ syncUserOffices n'a pas retourné un tableau", { type: typeof affiliations });
			await loadUserOfficesFromDatabase(store, userUid);
			return;
		}

		LoggerService.info("✅ Offices synchronisés depuis ID.NOT", { count: affiliations.length });

		try {
			await triggerNotaryEntitlementRefresh("IdNotOfficeSync", userUid);
		} catch (error: unknown) {
			LoggerService.warn("⚠️ Refresh droits après sync IdNot ignoré", { error, userUid });
		}

		await applyIdNotSyncedAffiliations(store, userUid, affiliations);
	} catch (error: unknown) {
		LoggerService.error("❌ Erreur lors de la synchronisation des offices", { error, userUid });
		store.error = "Impossible de synchroniser les offices";
		await loadUserOfficesFromDatabase(store, userUid);
		throw error;
	} finally {
		store.isLoading = false;
	}
}
